import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect } from "react";
import queries from "../queries";
import {
  calculateSubtotal,
  getCurrCurrency,
  haveLoginToken,
  isMobileScreen,
  orderStatus,
  paymentStatusColor,
  setPageTitle,
} from "../common";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./wishlist.css";
import Swal from "sweetalert2";
import { ImportMainJs } from "../helper/ImportScript";
import moment from "moment-timezone";
import * as LocalCartHelper from "../helper/LocalCartHelper";
import { useSessionStorage } from "../context/SessionStorageContext";

function Orders({ dataSelf, error, refetch }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionData, updateSessionStorage, isUserLoggedIn, localCart } =
    useSessionStorage();
  const [
    getOrders,
    { data: orderData, loading: orderLoading, error: orderError },
  ] = useLazyQuery(queries.member.GET_PURCHASED_ORDERS, {
    variables: { page: 1 },
  });
  const navigate = useNavigate();
  setPageTitle("My Orders | Order History & Tracking");
  const [addProductToOrders] = useMutation(
    queries.member.ADD_PRODUCT_TO_ORDERS
  );
  const [completePurchaseOrder] = useMutation(
    queries.member.COMPLETE_PENDING_ORDER
  );

  useEffect(() => {
    const purchasedItemId = searchParams.get("purchasedItemId");

    if (haveLoginToken() || purchasedItemId !== null) {
      handlePendingPurchase();
    } else {
      navigate("/");
    }
    getOrders();
  }, []);

  useEffect(() => {
    if (orderData != null) {
      ImportMainJs();
    }
  }, [orderData]);

  async function handlePendingPurchase() {
    const status = searchParams.get("status");
    const session_id = searchParams.get("session_id");
    const purchasedItemId = searchParams.get("purchasedItemId");
    if (status == "cancel") {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
      });
    } else if (purchasedItemId != null) {
      // For Square
      try {
        const { data } = await completePurchaseOrder({
          variables: {
            purchasedItemId: purchasedItemId,
          },
        });
        if (data?.completePurchaseOrder) {
          LocalCartHelper.clearLocalCart(updateSessionStorage);
          window.location.href = "/my-orders";
        }
      } catch (e) {
        if (e) {
          e = {
            message: e,
          };
        }
        if (e.message.graphQLErrors[0].message === "Order already placed") {
          Swal.fire("Order already placed", "", "success").then(() => {
            window.location.href = "/my-orders";
          });
        } else {
          Swal.fire({
            icon: "error",
            title: e.message.graphQLErrors[0].message
              ? e.message.graphQLErrors[0].message
              : e.message,
          });
        }
      }
    } else if (session_id != null) {
      // For Stripe
      try {
        const { data } = await addProductToOrders({
          variables: {
            sessionId: session_id,
          },
        });

        getOrders();
        Swal.fire({
          icon: "success",
          title: "Order placed successfully",
        }).then(() => {
          window.location.href = "/my-orders";
        });
      } catch (e) {
        if (e) {
          e = {
            message: e,
          };
        }
        if (e.message.graphQLErrors[0].message === "Order already placed") {
          Swal.fire("Order already placed", "", "success").then(() => {
            window.location.href = "/my-orders";
          });
        } else {
          Swal.fire({
            icon: "error",
            title: e.message,
          });
        }
      }
    }
  }

  return (
    <>
      <section
        className="breadcrumbs-custom"
        data-preset='{"title":"Breadcrumbs","category":"header","reload":false,"id":"breadcrumbs"}'
      >
        <div className="breadcrumbs-custom-container">
          <ul className="breadcrumbs-custom-path">
            <li>
              <a href="/">Home</a>
            </li>
            <li className="active">My Orders</li>
          </ul>
        </div>
      </section>
      <section
        className={isMobileScreen() ? "bg-white" : "section-sm bg-white"}
      >
        <div className="container">
          <h2 className="text-center">My Orders</h2>
          {dataSelf?.getSelf?.purchased?.length == 0 && (
            <div className="text-center">
              <h5 className="mt-5">
                Your haven't shopped with us yet. Checkout our most purchased
                products.
              </h5>
              <a className="btn btn-primary text-center" href="/products">
                Shop Now
              </a>
            </div>
          )}
          {dataSelf?.getSelf?.purchased?.map((purchasedItem) => {
            return (
              <div className="row order-item">
                <div className="col-12 order-item-title">
                  <div className="row">
                    <div className="col-md-8">
                      <h4>#{purchasedItem?._id}</h4>
                      <span
                        className="order-item-status"
                        style={{
                          backgroundColor:
                            paymentStatusColor[purchasedItem?.status],
                        }}
                      >
                        {orderStatus[purchasedItem?.status]}
                      </span>
                      {" " +
                        moment(purchasedItem?.date)
                          .local()
                          .format("Do MMMM, YYYY hh:mm a")}
                    </div>
                    <div className="col-md-4 text-right text-muted">
                      {purchasedItem?.tracking && (
                        <div className="col text-right">
                          <a
                            target="_blank"
                            href={purchasedItem?.tracking}
                            className="btn btn-primary"
                          >
                            Track Order
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12 p-0">
                  <div className="cart-main">
                    {purchasedItem?.products?.map((product) => {
                      return (
                        <div className="cart-item py-1 px-4 border-bottom-0">
                          <figure className="cart-item-col cart-item-col-figure">
                            <a
                              className="link-image rounded"
                              href={`/product/${encodeURIComponent(
                                product?.product?.slug
                              )}`}
                            >
                              <img
                                src={product?.product?.image?.[0]}
                                alt=""
                                width="260"
                                height="312"
                              />
                            </a>
                          </figure>
                          <div className="cart-item-col cart-item-col-content">
                            <div className="cart-item-title">
                              <a
                                href={`/product/${encodeURIComponent(
                                  product?.product?.slug
                                )}`}
                              >
                                {product?.product?.name}{" "}
                                {product?.options?.length > 0 ? " - " : ""}
                                {product?.options?.map((o) => o.value + " ")}
                              </a>
                            </div>
                          </div>
                          <div
                            className={`cart-item-col-price ${
                              product?.price === 0 ? "text-strike" : ""
                            }`}
                          >
                            {getCurrCurrency()}
                            {product?.price > 0
                              ? product?.price
                              : product?.product?.from_price}{" "}
                            x {product?.qty} {"= "}
                            <span className="cart-subtitle font-weight-bold">
                              {getCurrCurrency()}
                              {parseFloat(
                                (product?.price
                                  ? product.price
                                  : product?.product?.from_price) * product?.qty
                              ).toFixed(2)}
                            </span>
                          </div>
                          {product?.price === 0 && (
                            <div>
                              <span className="cart-subtitle font-weight-bold text-primary ml-2">
                                FREE
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="col-12 order-item-footer">
                  <div className="row">
                    <div className="col">
                      To, <br />
                      {purchasedItem?.address?.line1 +
                        ", " +
                        (purchasedItem?.address?.line2
                          ? purchasedItem?.address?.line2 + ", "
                          : "") +
                        purchasedItem?.address?.city +
                        ", " +
                        purchasedItem?.address?.state +
                        ", " +
                        purchasedItem?.address?.postal_code}
                    </div>
                    <div className="mr-4">
                      <div className="row mt-0 mr-0">
                        <span className="col">Subtotal:&nbsp;</span>
                        <span className="font-weight-bold cart-subtitle">
                          {getCurrCurrency()}
                          {calculateSubtotal(purchasedItem?.products)}
                        </span>
                      </div>
                      {purchasedItem?.taxAmount > 0 && (
                        <div className="row mt-0 mr-0">
                          <span className="col">Taxes:&nbsp;</span>
                          <span className="font-weight-bold cart-subtitle">
                            {getCurrCurrency()}
                            {purchasedItem?.taxAmount}
                          </span>
                        </div>
                      )}
                      {purchasedItem?.shippingAmount > 0 && (
                        <div className="row mt-0 mr-0">
                          <span className="col">Shipping:&nbsp;</span>
                          <span className="font-weight-bold cart-subtitle">
                            {getCurrCurrency()}
                            {purchasedItem?.shippingAmount}
                          </span>
                        </div>
                      )}
                      {purchasedItem?.priceBreakdown?.promotionDiscount > 0 && (
                        <div className="row mt-0 mr-0">
                          <span className="col">Discount:&nbsp;</span>
                          <span className="font-weight-bold cart-subtitle">
                            -{getCurrCurrency()}
                            {purchasedItem?.priceBreakdown?.promotionDiscount}
                          </span>
                        </div>
                      )}
                      <div className="row mt-0 mr-0">
                        <span className="col">Total:&nbsp;</span>
                        <span className="font-weight-bold cart-subtitle">
                          {getCurrCurrency()}
                          {purchasedItem?.amount}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

export default Orders;
